import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Check, X, Bot, Star, Sparkles, Wrench, Crown,
  Swords, Loader2, TrendingUp, TrendingDown, MoveHorizontal, Hand, Flame,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MissionSummary from "@/components/mission/MissionSummary";
import CallReasonStep from "@/components/mission/CallReasonStep";
import { TeachCard, MissionPrimer } from "@/components/mission/Teach";
import { BossReadiness } from "@/components/mission/Boss";
import ChartEngine from "@/components/ChartEngine";
import CandlePick from "@/components/CandlePick";

const MINI_TOGGLES = { volume: true, vwap: false, ema9: false, ema20: false, ema50: false, ema200: false, rsi: false };

export default function Mission() {
  const { missionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const saveKey = `tc_mp_${user?.id}_${missionId}`;
  const [m, setM] = useState(null);
  const [phase, setPhase] = useState("intro");
  const [idx, setIdx] = useState(0);
  const [results, setResults] = useState({});
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const attemptKey = useRef(window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  // per-step
  const [selected, setSelected] = useState(null);
  const [price, setPrice] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [okAnswer, setOkAnswer] = useState(false);
  const [mtfTf, setMtfTf] = useState("5m");
  const [mtfViewed, setMtfViewed] = useState(["5m"]);
  const [moved, setMoved] = useState(false);

  useEffect(() => { api.get(`/training/mission/${missionId}`).then((r) => setM(r.data)); }, [missionId]);

  const resetStep = () => { setSelected(null); setPrice(null); setMoved(false); setFeedback(null); setAttempts(0); setRevealed(false); setOkAnswer(false); setMtfTf("5m"); setMtfViewed(["5m"]); };

  const enterMarket = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/training/next-scenario?mission_id=${missionId}`);
      navigate(`/replay?scenario=${data.scenario_id}&mission=${missionId}`);
    } catch (e) { setBusy(false); }
  };

  const enterAnalyst = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/training/adaptive-next?mission_id=${missionId}&stage=3`);
      navigate(`/analyze?scenario=${data.scenario_id}&mission=${missionId}`);
    } catch (e) { setBusy(false); }
  };

  const enterBoss4 = () => navigate("/boss4");

  if (!m) return <div className="p-8 text-tc-muted font-mono text-sm">Loading mission…</div>;

  const steps = m.steps || [];
  const step = steps[idx];
  const isLast = idx === steps.length - 1;
  const chart = step?.chart_key ? m.charts[step.chart_key] : null;

  const startPrice = () => {
    if (price == null && chart) {
      const lo = Math.min(...chart.map((c) => c.low)), hi = Math.max(...chart.map((c) => c.high));
      setPrice(+((lo + hi) / 2).toFixed(2));
    }
  };

  const submit = async (answer) => {
    setBusy(true);
    const tries = attempts + 1;
    setAttempts(tries);
    try {
      const { data } = await api.post(`/training/mission/${missionId}/check`, { step_id: step.id, answer });
      setFeedback(data);
      if (data.correct) {
        setOkAnswer(true);
        setResults((r) => ({ ...r, [step.id]: { step_id: step.id, correct: true, attempts: tries } }));
      } else if (tries >= 2) {
        setRevealed(true);
        setResults((r) => ({ ...r, [step.id]: { step_id: step.id, correct: false, attempts: tries } }));
      }
    } catch (e) {
      setAttempts(tries - 1);
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  const next = async () => {
    if (isLast) {
      setBusy(true);
      try {
        // Correctness is taken from the server's own answer checks; the key makes this attempt single-use.
        const payload = { attempt_key: attemptKey.current };
        const { data } = await api.post(`/training/mission/${missionId}/complete`, payload);
        localStorage.removeItem(saveKey);
        setSummary(data);
        setPhase("summary");
      } catch (e) {
        toast.error(apiErr(e));
        if (e?.response?.status === 400) {  // some steps were never checked on the server: restart this run cleanly
          localStorage.removeItem(saveKey);
          await api.post(`/training/mission/${missionId}/fresh-start`).catch(() => {});
          setIdx(0); setResults({}); resetStep(); setPhase("intro");
        }
      } finally { setBusy(false); }
    } else {
      localStorage.setItem(saveKey, JSON.stringify({ idx: idx + 1, results }));
      setIdx(idx + 1);
      resetStep();
    }
  };

  const saved = (() => { try { return JSON.parse(localStorage.getItem(saveKey) || "null"); } catch { return null; } })();
  const startSteps = async () => {
    if (saved && saved.idx > 0 && saved.idx < steps.length) { setIdx(saved.idx); setResults(saved.results || {}); }
    else await api.post(`/training/mission/${missionId}/fresh-start`).catch(() => {});  // new run: clear old server checks
    setPhase("step");
  };
  const exitMission = () => {
    if (idx > 0) toast.success("Your finished steps are saved");
    navigate("/training");
  };

  // ---------------- INTRO ----------------
  if (phase === "intro") {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto">
        <button onClick={() => navigate("/training")} className="text-sm text-tc-muted hover:text-tc-txt flex items-center gap-1.5 mb-6"><ArrowLeft className="w-4 h-4" /> Trader Path</button>
        <div className="tc-card p-8 text-center animate-fade-up">
          <div className={`w-16 h-16 rounded-2xl border flex items-center justify-center mx-auto mb-5 ${m.is_boss ? "bg-tc-xp/15 border-tc-xp/30 text-tc-xp" : "bg-tc-coach/15 border-tc-coach/30 text-tc-coach"}`}>
            {m.is_boss ? <Swords className="w-8 h-8" /> : <span className="font-mono font-bold text-lg">{String(m.number).padStart(2, "0")}</span>}
          </div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">{m.is_boss ? "BOSS MARKET" : `Mission ${String(m.number).padStart(2, "0")}`} · {m.asset}</div>
          <h1 className={`font-heading text-3xl font-bold tracking-tight mt-1 ${m.is_boss ? "text-tc-xp" : ""}`}>{m.name}</h1>
          <p className="text-tc-sub mt-4 leading-relaxed">{m.intro}</p>
          <MissionPrimer purpose={m.purpose} primer={m.primer} />
          {m.is_boss && <BossReadiness items={m.readiness} />}
          <div className="flex justify-center gap-3 mt-5 text-xs font-mono text-tc-muted">
            <span className="text-tc-xp">+{m.xp} XP</span><span>· {m.est}</span>
          </div>
          <button data-testid="mission-start-btn" onClick={m.type === "market" ? enterMarket : m.type === "analyst" ? enterAnalyst : m.type === "risk" ? enterBoss4 : startSteps} disabled={busy || m.locked}
            className={`mt-6 w-full flex items-center justify-center gap-2 rounded-lg font-semibold py-3 transition-colors disabled:opacity-60 ${m.is_boss ? "bg-tc-xp text-tc-bg hover:bg-tc-xp/90" : "bg-tc-coach text-tc-bg hover:bg-tc-coach/90"}`}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {m.locked ? "LOCKED — finish the earlier missions first" : (m.type === "market" || m.type === "analyst" || m.type === "risk") ? "ENTER MARKET" : m.is_boss ? "ENTER MARKET" : saved?.idx > 0 ? "RESUME TRAINING" : "START TRAINING"} {!m.locked && <ArrowRight className="w-4 h-4" />}
          </button>
          {(m.type === "market" || m.type === "analyst" || m.type === "risk") && <p className="text-[11px] text-tc-muted mt-3">Real historical data · future hidden · your decision is scored on process, not profit.</p>}
        </div>
      </div>
    );
  }

  // ---------------- SUMMARY ----------------
  if (phase === "summary" && summary) {
    return <MissionSummary m={m} s={summary} navigate={navigate} />;
  }

  // ---------------- STEP ----------------
  const canContinue = step.type === "teach" || okAnswer || revealed;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <button data-testid="mission-exit-btn" onClick={exitMission} className="text-sm text-tc-muted hover:text-tc-txt flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" /> Exit to Trader Path</button>
      {/* progress */}
      <div className="flex items-center gap-1.5 mb-5">
        {steps.map((s, i) => (
          <div key={s.id} className={`h-1.5 rounded-full flex-1 transition-all ${i < idx ? "bg-tc-bull" : i === idx ? "bg-tc-coach" : "bg-white/10"}`} />
        ))}
      </div>

      <div key={step.id} className="tc-card p-5 sm:p-6 animate-fade-up">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach">{m.name}</div>
        <h2 className="font-heading text-xl sm:text-2xl font-bold mt-1">{step.title}</h2>
        {step.practice && <div className="inline-block mt-2 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-tc-coach/10 text-tc-coach border border-tc-coach/30" data-testid="practice-badge">Practice · not scored</div>}

        {step.type === "call_reason" && (
          <>
            {chart && <div className="mt-4"><ChartEngine candles={chart} toggles={MINI_TOGGLES} symbol="BTC/USD" interval="5m" height={240} hideHeader /></div>}
            <CallReasonStep step={step} feedback={feedback} locked={okAnswer || revealed} busy={busy} onSubmit={submit} />
          </>
        )}

        {step.type === "teach" && (
          <>
            {chart && <div className="mt-4"><ChartEngine candles={chart} toggles={{ ...MINI_TOGGLES, ...(step.toggles || {}) }} symbol="BTC/USD" interval={step.interval || "5m"} height={240} hideHeader /></div>}
            <TeachCard step={step} />
          </>
        )}

        {(step.type === "choice" || step.type === "mastery_choice" || step.type === "chart_choice" || step.type === "decision" || step.type === "mtf_choice") && (() => {
          const isMtf = step.type === "mtf_choice";
          const mtfCharts = isMtf ? (m.mtf_charts?.[step.mtf_key] || {}) : {};
          const tfOrder = isMtf ? ["5m", "1h", "4h"].filter((tf) => mtfCharts[tf]) : [];
          const allViewed = !isMtf || tfOrder.every((tf) => mtfViewed.includes(tf));
          const optDisabled = okAnswer || busy || (isMtf && !allViewed);
          return (
          <>
            {isMtf ? (
              <>
                <div className="flex gap-2 mt-4" data-testid="mtf-switcher">
                  {tfOrder.map((tf) => (
                    <button key={tf} data-testid={`mtf-tf-${tf}`}
                      onClick={() => { setMtfTf(tf); setMtfViewed((v) => v.includes(tf) ? v : [...v, tf]); }}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-mono transition-colors ${mtfTf === tf ? "bg-tc-coach text-tc-bg" : "bg-white/5 text-tc-sub hover:text-tc-txt"} ${mtfViewed.includes(tf) ? "" : "ring-1 ring-tc-coach/40"}`}>
                      {tf.toUpperCase()}
                    </button>
                  ))}
                </div>
                <div className="mt-3"><ChartEngine candles={mtfCharts[mtfTf]} toggles={MINI_TOGGLES} symbol="BTC/USD" interval={mtfTf} height={240} hideHeader /></div>
              </>
            ) : (
              chart && <div className="mt-4"><ChartEngine candles={chart} toggles={{ ...MINI_TOGGLES, ...(step.toggles || {}) }} symbol="BTC/USD" interval={step.interval || "5m"} height={240} hideHeader /></div>
            )}
            <p className="text-tc-sub mt-4 font-medium">{step.prompt}</p>
            {isMtf && !allViewed && <p className="text-tc-coach text-[13px] mt-2">Switch to every timeframe before you answer.</p>}
            <div className="grid gap-2 mt-3">
              {step.options.map((o, i) => {
                const isSel = selected === i;
                const showCorrect = (revealed || okAnswer) && i === feedback?.correct_answer;
                const showWrong = feedback && !feedback.correct && isSel && !showCorrect;
                return (
                  <button key={i} data-testid={`option-${i}`} disabled={optDisabled}
                    onClick={() => { setSelected(i); submit(i); }}
                    className={`text-left px-4 py-3 rounded-lg border text-sm flex items-center justify-between transition-colors ${
                      showCorrect ? "border-tc-bull/60 bg-tc-bull/10 text-tc-bull"
                        : showWrong ? "border-tc-bear/60 bg-tc-bear/10 text-tc-bear"
                        : isSel ? "border-tc-coach bg-tc-coach/10" : "border-white/10 hover:border-white/30 text-tc-sub"} ${optDisabled && !showCorrect && !showWrong ? "opacity-60" : ""}`}>
                    {o}
                    {showCorrect && <Check className="w-4 h-4" />}{showWrong && <X className="w-4 h-4" />}
                  </button>
                );
              })}
            </div>
          </>
          );
        })()}

        {step.type === "candle_pick" && (
          <>
            <p className="text-tc-sub mt-4 font-medium">{step.prompt}</p>
            <div className="mt-3">
              <CandlePick candles={chart} disabled={okAnswer || busy}
                pickedIndex={selected} correctIndex={(revealed || okAnswer) ? feedback?.correct_answer : undefined}
                onPick={(i) => { setSelected(i); submit(i); }} />
            </div>
          </>
        )}

        {step.type === "draw_level" && (
          <>
            {startPrice()}
            <p className="text-tc-sub mt-4 font-medium">{step.prompt}</p>
            <div className="mt-3">
              <ChartEngine candles={chart} toggles={MINI_TOGGLES} symbol="BTC/USD" interval="5m" height={240} hideHeader
                lines={[{ price: price, color: step.line_color, title: "YOUR LINE" },
                        ...(revealed || okAnswer ? [{ price: feedback?.correct_answer, color: "#F8FAFC", title: "ANSWER" }] : [])]} />
            </div>
            {price != null && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-sm font-mono">
                  <span className="text-tc-muted flex items-center gap-1.5"><MoveHorizontal className="w-4 h-4" /> Line price</span>
                  <span className="text-tc-txt">${price.toLocaleString()}</span>
                </div>
                {(() => {
                  const lo = Math.min(...chart.map((c) => c.low));
                  const hi = Math.max(...chart.map((c) => c.high));
                  const pad = (hi - lo) * 0.08;
                  return (
                    <input data-testid="level-slider" type="range" disabled={okAnswer}
                      min={lo - pad} max={hi + pad} step={(hi - lo) / 200}
                      value={price} onChange={(e) => { setPrice(+e.target.value); setMoved(true); }}
                      className="w-full mt-2 accent-tc-coach" />
                  );
                })()}
                {!okAnswer && (
                  <button data-testid="submit-level-btn" disabled={busy || !moved} onClick={() => submit(price)}
                    className="mt-3 w-full rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 hover:bg-tc-coach/90 disabled:opacity-50">
                    {moved ? "Place Line" : "Drag the line to a level first"}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* feedback */}
        {feedback && (
          <div className={`mt-4 rounded-lg p-3.5 text-sm ${feedback.correct ? "bg-tc-bull/10 border border-tc-bull/30" : "bg-white/5 border border-white/10"}`} data-testid="step-feedback">
            <div className={`flex items-center gap-2 font-semibold ${feedback.correct ? "text-tc-bull" : "text-tc-pass"}`}>
              {feedback.correct ? <><Check className="w-4 h-4" /> Correct</> : <><Bot className="w-4 h-4" /> Craft Coach</>}
            </div>
            <p className="text-tc-sub mt-1.5 leading-relaxed">{feedback.explanation}</p>
            {!feedback.correct && !revealed && feedback.hint && (
              <p className="text-tc-coach mt-2 text-[13px]">💡 {feedback.hint} — try again.</p>
            )}
          </div>
        )}

        {canContinue && (
          <button data-testid="mission-continue-btn" disabled={busy} onClick={next}
            className="mt-5 w-full flex items-center justify-center gap-2 rounded-lg bg-tc-coach text-tc-bg font-semibold py-3 hover:bg-tc-coach/90 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {isLast ? (m.is_boss ? "See Your Result" : "Complete Mission") : "Continue"} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
